import { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectFade } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import styles from "./Hero.module.css";

import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { api } from "../../store/slices/authSlice";
import { useNavigate } from "react-router-dom";

export default function Hero() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();

  const [banners, setBanners] = useState([]);

  const [targetStats, setTargetStats] = useState({
    materials: 200,
    suppliers: 150,
  });

  const [displayStats, setDisplayStats] = useState({
    materials: 200,
    suppliers: 200,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHeroData = async () => {
      try {
        const [bannersRes, suppliersRes, materialsRes] =
          await Promise.allSettled([
            api.get("/banners"),
            api.get("/suppliers"),
            api.get("/materials"),
          ]);

        if (
          bannersRes.status === "fulfilled" &&
          bannersRes.value.data.code === 200
        ) {
          setBanners(bannersRes.value.data.data || []);
        }

    
        let suppliersCount = 0;

        if (
          suppliersRes.status === "fulfilled" &&
          suppliersRes.value.data?.code === 200
        ) {
          suppliersCount =
            suppliersRes.value.data?.pagination?.total ?? 0;
        }

     
        let materialsCount = 0;

        if (
          materialsRes.status === "fulfilled" &&
          materialsRes.value.data?.code === 200
        ) {
          materialsCount =
            materialsRes.value.data?.pagination?.total ?? 0;
        }

  
        setTargetStats({
          suppliers: 150 + suppliersCount,
          materials: 200 + materialsCount,
        });
      } catch (err) {
        console.error("Error fetching hero data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHeroData();
  }, []);


  useEffect(() => {
    const startValues = {
      materials: 200,
      suppliers: 150,
    };

    const duration = 1800; 
    const startTime = performance.now();

    let animationFrame;

    const animateCounters = (currentTime) => {
      const elapsed = currentTime - startTime;

      const progress = Math.min(elapsed / duration, 1);

      const easedProgress =
        1 - Math.pow(1 - progress, 3);

      const materialsValue = Math.floor(
        startValues.materials +
          (targetStats.materials - startValues.materials) *
            easedProgress
      );

      const suppliersValue = Math.floor(
        startValues.suppliers +
          (targetStats.suppliers - startValues.suppliers) *
            easedProgress
      );

      setDisplayStats({
        materials: materialsValue,
        suppliers: suppliersValue,
      });

      if (progress < 1) {
        animationFrame =
          requestAnimationFrame(animateCounters);
      }
    };

    animationFrame =
      requestAnimationFrame(animateCounters);

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [targetStats]);

  if (loading) {
    return <div className={styles.loader}>...</div>;
  }

  return (
    <section
      className={styles.heroSection}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="container">
        <div className={styles.heroWrapper}>
          <div className={styles.fullHeroGrid}>

            
            <div className={styles.infoPart}>
              <span className={styles.badge}>
                {t("hero.badge")}
              </span>

              <h1 className={styles.titleAnim}>
                {t("hero.title")}
              </h1>

              <p>{t("hero.description")}</p>

              <div className={styles.heroActions}>
                <button
                  className={styles.ctaButton}
                  onClick={() => navigate("/top-formula")}
                >
                  {t("hero.cta")}

                  {isRtl ? (
                    <FaArrowLeft />
                  ) : (
                    <FaArrowRight />
                  )}
                </button>
              </div>

      
              <div className={styles.stats}>
                <div className={styles.statItem}>
                  <strong>
                    +{displayStats.materials}
                  </strong>

                  <span>
                    {t("hero.stats.materials")}
                  </span>
                </div>

                <div className={styles.statItem}>
                  <strong>
                    +{displayStats.suppliers}
                  </strong>

                  <span>
                    {t("hero.stats.suppliers")}
                  </span>
                </div>
              </div>
            </div>

       
            <div className={styles.sliderPart}>
              <Swiper
                key={i18n.language}
                modules={[
                  Autoplay,
                  Pagination,
                  EffectFade,
                ]}
                effect="fade"
                fadeEffect={{
                  crossFade: true,
                }}
                autoplay={{
                  delay: 5000,
                  disableOnInteraction: false,
                }}
                pagination={{
                  clickable: true,
                }}
                className={styles.mainSwiper}
                speed={600}
              >
                {banners.map((screen, idx) => (
                  <SwiperSlide key={idx}>
                    <div className={styles.imageWrapper}>
                      <img
                        src={screen.banner}
                        alt={`banner-${idx}`}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
